import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { recipesAPI } from '../utils/api';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const res = await recipesAPI.getById(id);
        setRecipe(res.data.data);
      } catch (err) {
        setError('ไม่พบสูตรที่ต้องการ หรือเกิดข้อผิดพลาดในการดึงข้อมูล');
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await recipesAPI.export(id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recipe-${recipe.name}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการส่งออก Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`ต้องการลบสูตร "${recipe.name}" ใช่หรือไม่?`)) return;
    setDeleting(true);
    try {
      await recipesAPI.delete(id);
      navigate('/recipes');
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการลบสูตร');
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(amount || 0);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('th-TH', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  if (loading) {
    return (
      <Container fluid className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error && !recipe) {
    return (
      <Container fluid className="main-content">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/recipes')}>
          <i className="bi bi-arrow-left me-2"></i>กลับ
        </Button>
      </Container>
    );
  }

  const vatItems = recipe.items.filter(i => i.hasVat);
  const nonVatItems = recipe.items.filter(i => !i.hasVat);
  const subtotalVat = vatItems.reduce((s, i) => s + (i.totalCost || 0), 0);
  const subtotalNonVat = nonVatItems.reduce((s, i) => s + (i.totalCost || 0), 0);
  const baseTotal = Math.max(0, subtotalVat - (recipe.discountVat || 0)) + Math.max(0, subtotalNonVat - (recipe.discountNonVat || 0));
  const markupAmount = baseTotal * ((recipe.markupPercent || 0) / 100);

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="mb-2 no-print"
                onClick={() => navigate('/recipes')}
              >
                <i className="bi bi-arrow-left me-1"></i>
                กลับรายการสูตร
              </Button>
              <h2>
                <i className="bi bi-journal-text me-2"></i>
                {recipe.name}
              </h2>
              {recipe.description && (
                <p className="text-muted">{recipe.description}</p>
              )}
              <p className="text-muted small">
                สร้างโดย <strong>{recipe.createdBy?.fullName || recipe.createdBy?.username}</strong>
                {' · '}
                {formatDate(recipe.createdAt)}
              </p>
            </div>
            <div className="d-flex gap-2 no-print">
              <Button
                variant="outline-primary"
                onClick={() => navigate(`/recipes/${id}/edit`)}
              >
                <i className="bi bi-pencil me-1"></i>
                แก้ไข
              </Button>
              <Button
                variant="outline-success"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <><Spinner size="sm" animation="border" className="me-1" />กำลังส่งออก...</>
                ) : (
                  <><i className="bi bi-file-earmark-excel me-1"></i>Excel</>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setTimeout(() => window.print(), 100)}
              >
                <i className="bi bi-printer me-1"></i>
                พิมพ์
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <><Spinner size="sm" animation="border" className="me-1" />กำลังลบ...</>
                ) : (
                  <><i className="bi bi-trash me-1"></i>ลบ</>
                )}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <i className="bi bi-list-ul me-2"></i>
              รายการวัตถุดิบ ({recipe.items.length} รายการ)
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>รหัส</th>
                    <th>วัตถุดิบ</th>
                    <th>หมวดหมู่</th>
                    <th className="text-center">หน่วย</th>
                    <th className="text-end">ปริมาณ</th>
                    <th className="text-end">ราคา/หน่วย</th>
                    <th className="text-center">VAT</th>
                    <th className="text-end">รวม</th>
                    <th>หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  {recipe.items.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="text-muted">{idx + 1}</td>
                      <td><code>{item.material?.code}</code></td>
                      <td><strong>{item.material?.name}</strong></td>
                      <td className="text-muted small">{item.material?.category || '-'}</td>
                      <td className="text-center">{item.material?.unit}</td>
                      <td className="text-end">{item.quantity}</td>
                      <td className="text-end">{formatCurrency(item.unitCost)}</td>
                      <td className="text-center">
                        {item.hasVat
                          ? <Badge bg="warning" text="dark">VAT</Badge>
                          : <Badge bg="light" text="muted">-</Badge>}
                      </td>
                      <td className="text-end fw-bold">{formatCurrency(item.totalCost)}</td>
                      <td className="text-muted small">{item.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="justify-content-end">
        <Col md={5}>
          <Card className="border-success">
            <Card.Header className="bg-success text-white">
              <i className="bi bi-calculator me-2"></i>
              สรุปต้นทุน
            </Card.Header>
            <Card.Body>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  {subtotalNonVat > 0 && (
                    <tr>
                      <td>รวมรายการไม่มี VAT</td>
                      <td className="text-end">{formatCurrency(subtotalNonVat)}</td>
                    </tr>
                  )}
                  {recipe.discountNonVat > 0 && (
                    <tr className="text-danger">
                      <td>ส่วนลด (ไม่มี VAT)</td>
                      <td className="text-end">-{formatCurrency(recipe.discountNonVat)}</td>
                    </tr>
                  )}
                  {subtotalVat > 0 && (
                    <tr>
                      <td>รวมรายการมี VAT</td>
                      <td className="text-end">{formatCurrency(subtotalVat)}</td>
                    </tr>
                  )}
                  {recipe.discountVat > 0 && (
                    <tr className="text-danger">
                      <td>ส่วนลด (มี VAT)</td>
                      <td className="text-end">-{formatCurrency(recipe.discountVat)}</td>
                    </tr>
                  )}
                  {(recipe.markupPercent || 0) > 0 && (
                    <tr className="text-info">
                      <td>บวกเพิ่ม ({recipe.markupPercent}%)</td>
                      <td className="text-end">+{formatCurrency(markupAmount)}</td>
                    </tr>
                  )}
                  <tr className="border-top">
                    <td><strong>ต้นทุนรวมทั้งหมด</strong></td>
                    <td className="text-end">
                      <strong className="text-success fs-5">{formatCurrency(recipe.totalCost)}</strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RecipeDetail;
